import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookOpen } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { PianoKeyboard } from "@/components/PianoKeyboard";
import { StaffViewer } from "@/components/StaffViewer";
import { ARTICLES } from "@/data/articles";

const letter = (p) => p.replace(/\d/g, "").replace("#", "♯").replace(/^([A-G])b$/, "$1♭");

const Block = ({ b, i }) => {
  if (b.h) return <h2 className="mt-8 font-serif text-2xl">{b.h}</h2>;
  if (b.p) return <p className="mt-4 text-base leading-relaxed">{b.p}</p>;
  if (b.list) return <ul className="mt-4 list-disc space-y-1.5 pl-6">{b.list.map((x) => <li key={x}>{x}</li>)}</ul>;
  return (
    <figure className="mt-5 rounded-2xl border border-border bg-card p-3" data-testid={`article-figure-${i}`}>
      {b.keyboard && <div className="overflow-x-auto"><div className="min-w-[18rem]"><PianoKeyboard {...b.keyboard} /></div></div>}
      {b.staff && <div className="mx-auto max-w-md"><StaffViewer clef={b.staff.clef} notes={b.staff.notes.map((p) => ({ pitch: p, label: letter(p), duration: "whole" }))} width={360} testId={`article-staff-${i}`} /></div>}
      {b.caption && <figcaption className="mt-2 text-sm text-muted-foreground">{b.caption}</figcaption>}
    </figure>
  );
};

const ArticleList = () => (
  <>
    <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">Music theory articles</p>
    <h1 className="mt-3 font-serif text-4xl sm:text-5xl">Music theory, explained simply</h1>
    <p className="mt-3 text-muted-foreground">Short guides from Jason's lessons. Each one links to the course where you can practise it with games, quizzes and play-alongs.</p>
    <ul className="mt-8 grid gap-4 sm:grid-cols-2">
      {ARTICLES.map((a) => (
        <li key={a.slug}>
          <Link to={`/articles/${a.slug}`} data-testid={`article-card-${a.slug}`} className="block h-full rounded-2xl border border-border bg-card p-5 transition-colors hover:border-primary">
            <BookOpen size={18} className="text-primary" />
            <h2 className="mt-2 font-serif text-xl">{a.title}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{a.description}</p>
          </Link>
        </li>
      ))}
    </ul>
  </>
);

const Article = ({ a }) => {
  const others = ARTICLES.filter((x) => x.slug !== a.slug).slice(0, 3);
  return (
    <article data-testid="article-page">
      <Link to="/articles" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary" data-testid="article-back"><ArrowLeft size={14} /> All articles</Link>
      <h1 className="mt-4 font-serif text-4xl sm:text-5xl" data-testid="article-title">{a.title}</h1>
      <p className="mt-3 text-lg text-muted-foreground">{a.description}</p>
      {a.blocks.map((b, i) => <Block key={i} b={b} i={i} />)}
      <Link to={a.course.to} data-testid="article-course-link" className="mt-10 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-primary-foreground transition-transform hover:scale-105">{a.course.label} <ArrowRight size={15} /></Link>
      <div className="mt-12 border-t border-border pt-6">
        <p className="font-mono text-xs uppercase tracking-widest text-primary">More articles</p>
        <ul className="mt-3 space-y-2">{others.map((o) => <li key={o.slug}><Link to={`/articles/${o.slug}`} className="font-semibold hover:text-primary" data-testid={`article-more-${o.slug}`}>{o.title} →</Link></li>)}</ul>
      </div>
    </article>
  );
};

export default function Articles() {
  const { slug } = useParams();
  const a = slug && ARTICLES.find((x) => x.slug === slug);
  if (slug && !a) return <Navigate to="/articles" replace />;
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <main className="mx-auto max-w-3xl px-4 py-10 sm:px-8 lg:py-14">{a ? <Article a={a} /> : <ArticleList />}</main>
    </div>
  );
}
